import React from 'react'
import { AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Badge,
} from '../ui'
import { useDeleteDepartment, type Department } from '../../hooks/useDepartments'
import { LoadingSpinner } from '../ui/loading-states'

interface DeleteDepartmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  department: Department | null
}

export function DeleteDepartmentDialog({
  open,
  onOpenChange,
  department
}: DeleteDepartmentDialogProps) {
  const deleteDepartment = useDeleteDepartment()
  const isLoading = deleteDepartment.isPending

  const handleDelete = async () => {
    if (!department) return

    try {
      await deleteDepartment.mutateAsync(department.id)
      onOpenChange(false)
    } catch (error) {
      // Error handling will be done by React Query and shown in UI
      console.error('Delete error:', error)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  if (!department) return null

  const getDepartmentHierarchy = (dept: Department) => {
    const parts = []
    if (dept.departments) {
      parts.push(dept.departments.name)
    }
    parts.push(dept.name)
    return parts.join(' → ')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <DialogTitle>
                <span className="text-red-900">Удалить подразделение?</span>
              </DialogTitle>
              <DialogDescription>
                Это действие нельзя будет отменить.
              </DialogDescription>
            </div>
          </div>
          {!isLoading && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <div className="py-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Название:</span>
                <p className="text-sm text-gray-900">{getDepartmentHierarchy(department)}</p>
              </div>

              {department.code_1c && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Код 1С:</span>
                  <p className="text-sm text-gray-900">{department.code_1c}</p>
                </div>
              )}

              <div>
                <span className="text-sm font-medium text-gray-700">Источник:</span>
                <div className="mt-1">
                  {department.code_1c ? (
                    <Badge variant="secondary">1С</Badge>
                  ) : (
                    <Badge variant="outline">TCO</Badge>
                  )}
                </div>
              </div>

              {department.employees_departments_manager_idToemployees && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Руководитель:</span>
                  <p className="text-sm text-gray-900">
                    {department.employees_departments_manager_idToemployees.last_name}{' '}
                    {department.employees_departments_manager_idToemployees.first_name}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Warning about consequences */}
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-start">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Внимание!</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Подразделение будет удалено безвозвратно</li>
                  <li>Дочерние подразделения могут потерять связь</li>
                  <li>Сотрудники останутся без подразделения</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Show API errors */}
          {deleteDepartment.error && (
            <div className="mt-4 flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(deleteDepartment.error as any)?.message || 'Произошла ошибка при удалении'}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Отменить
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Удаление...
              </>
            ) : (
              'Удалить'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}